import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Chip,
  Grid,
  CardMedia,
  Button,
  Stack,
  Dialog,
  DialogContent,
  IconButton,
  DialogTitle,
  Skeleton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import API from "../config/axios";
import { api_paths } from "../config/api.paths";
import SharePopover from "./SharePopover";
import { Link, useNavigate } from "react-router-dom";
import ProjectCard from "./ProjectCard";
import ProjectLinks from "./projects/ProjectLinks";
import { cleanHTML, getImageUrl } from "../utils/utils";

const PortfolioSection = ({ data }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [selectedFilter, setSelectedFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [filters, setFilters] = useState([]);
  const [projects, setProjects] = useState([]);
  const navigate = useNavigate();

  const isLoading = data === undefined;

  const handleOpen = (project) => {
    setSelectedProject(project);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedProject(null);
  };

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await API.get(api_paths.CATEGORY.get);
        const categoryList = response.data?.payload?.data?.categories || [];

        setFilters([
          { _id: "all", name: t("pages.home.portfolio.filters.all") },
          ...categoryList,
        ]);
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    };

    fetchCategories();
  }, [t]);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const params = {
          limit: 6,
          isActive: true,
        };

        if (selectedFilter !== "all") {
          params.category = selectedFilter;
        }

        const response = await API.get(api_paths.PROJECT.get, { params });
        const projectList = response.data?.payload?.data?.projects || [];
        setProjects(projectList);
      } catch (error) {
        console.error("Error fetching projects:", error);
      }
    };

    fetchProjects();
  }, [selectedFilter]);

  const filteredProjects = projects;

  return (
    <Box
      id="portfolio"
      sx={{ py: 10, backgroundColor: theme.palette.background.default }}
    >
      <Container maxWidth="xl">
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Chip
            label={t("pages.home.portfolio.chip_title")}
            sx={{
              fontFamily: "Syne, sans-serif",
              backgroundColor: theme.palette.background.chip,
              color: theme.palette.custom.chipText,
              fontWeight: 500,
              fontSize: "0.875rem",
              lineHeight: "1.25rem",
              mb: 2,
              p: 1,
            }}
          />
          <Typography
            variant="h2"
            component="h2"
            fontWeight="bold"
            sx={{
              background: theme.palette.custom.headingGradient,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              textAlign: "center",
              width: { xs: "100%", sm: "80%", md: "60%", lg: "50%" },
              margin: "0 auto",
              fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem", lg: "3rem" },
              lineHeight: { xs: "2rem", sm: "2.25rem", md: "2.5rem", lg: "1" },
            }}
          >
            {isLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Skeleton variant="text" height={60} width="80%" />
              </Box>
            ) : (
              data?.meta?.title || t("pages.home.portfolio.headline")
            )}
          </Typography>
          <Typography
            variant="p"
            component="p"
            color={theme.palette.text.secondary}
            sx={{
              mt: 2,
              mb: 6,
              px: { xs: 2, md: 0 },
              fontSize: "1.125rem",
              lineHeight: "1.75rem",
            }}
          >
            {isLoading ? (
              <Skeleton variant="text" height={30} width="100%" />
            ) : (
              data?.meta?.description || t("pages.home.portfolio.subheadline")
            )}
          </Typography>

          <Stack
            direction="row"
            spacing={2}
            justifyContent="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 6 }}
          >
            {filters.map((filter) => (
              <Button
                key={filter._id}
                onClick={() => setSelectedFilter(filter._id)}
                variant={selectedFilter === filter._id ? "contained" : "text"}
                sx={{
                  borderRadius: "20px",
                  textTransform: "none",
                  px: 2,
                  py: 1,
                  minWidth: "auto",
                  backgroundColor:
                    selectedFilter === filter._id
                      ? theme.palette.primary.main
                      : theme.palette.background.section,

                  color:
                    selectedFilter === filter._id
                      ? theme.palette.primary.contrastText
                      : theme.palette.text.secondary,

                  boxShadow:
                    selectedFilter === filter._id
                      ? theme.palette.custom.activeBlueShadow
                      : "none",
                  mb: 1,
                  "&:hover": {
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.primary.contrastText,
                  },
                }}
              >
                {filter.name}
              </Button>
            ))}
          </Stack>
        </Box>

        {filteredProjects.length > 0 ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
              },
              gap: 4,
            }}
          >
            {filteredProjects.map((project) => (
              <ProjectCard
                key={project._id}
                project={project}
                onViewDetails={handleOpen}
              />
            ))}
          </Box>
        ) : (
          <Box>
            <Typography variant="h6" color="text.secondary" textAlign="center">
              {t("pages.home.portfolio.no_projects")}
            </Typography>
          </Box>
        )}

        <Box sx={{ textAlign: "center", mt: 8 }}>
          <Button
            variant="outlined"
            endIcon={<OpenInNewIcon />}
            sx={{
              borderColor: theme.palette.custom.viewAllBtnBorder,
              color: theme.palette.custom.viewAllBtnText,
              backgroundColor: theme.palette.custom.viewAllBtnBg,
              px: 3,
              py: 1,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 500,
              borderWidth: "1px",
              "&:hover": {
                backgroundColor: theme.palette.custom.viewAllBtnHoverBg,
                color: theme.palette.custom.viewAllBtnHoverText,
                borderColor: theme.palette.custom.viewAllBtnHoverBg,
              },
            }}
            onClick={() => {
              const selectedCat = filters.find((f) => f._id === selectedFilter);
              const categoryName = selectedFilter !== "all" ? selectedCat?.name : "All Categories";
              const searchParams = new URLSearchParams();
              if (categoryName !== "All Categories") {
                searchParams.set("category", categoryName);
              }
              navigate({
                pathname: "/projects",
                search: searchParams.toString()
              });
            }}
          >
            {t("pages.home.portfolio.view_all_projects")}
          </Button>
        </Box>
      </Container>

      {/* Project Details Modal */}
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            overflow: "hidden",
          },
        }}
      >
        {selectedProject && (
          <>
            <DialogTitle
              sx={{
                m: 0,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography
                variant="h5"
                component="div"
                fontWeight={600}
                typography="text2xl"
                fontFamily={theme.typography.h3.fontFamily}
              >
                {selectedProject?.title}
              </Typography>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <SharePopover
                  url={window.location.href}
                  title={selectedProject?.title}
                />

                <IconButton
                  aria-label="close"
                  onClick={handleClose}
                  sx={{
                    color: (theme) => theme.palette.grey[500],
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>
            <DialogContent sx={{ p: 4 }}>
              <CardMedia
                component="img"
                image={getImageUrl(selectedProject?.image_path)}
                alt={selectedProject?.title}
                sx={{
                  width: "100%",
                  height: "auto",
                  maxHeight: "400px",
                  objectFit: "contain",
                  borderRadius: "12px",
                  mb: 4,
                }}
              />

              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box
                    sx={{
                      backgroundColor: theme.palette.background.section,
                      p: 2,
                      borderRadius: "0.75rem",
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      fontWeight={600}
                      typography="textSm"
                    >
                      {t("pages.home.portfolio.modal.client")}
                    </Typography>
                    <Typography fontWeight={600}>
                      {selectedProject?.client}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <Box
                    sx={{
                      backgroundColor: theme.palette.background.section,
                      p: 2,
                      borderRadius: "0.75rem",
                    }}
                  >
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      fontWeight={600}
                      typography="textSm"
                    >
                      {t("pages.home.portfolio.modal.duration")}
                    </Typography>
                    <Typography fontWeight={600}>
                      {selectedProject?.duration}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>

              <Box sx={{ mb: 4 }}>
                <Typography fontWeight={600} sx={{ mb: 1 }}>
                  {t("pages.home.portfolio.modal.about")}
                </Typography>
                <Box
                  sx={{
                wordBreak: "break-word",       // ← fixes overflow on Image 2
                overflowWrap: "break-word",    // ← extra safety for long words
                whiteSpace: "normal",          // ← ensures text wraps normally
                maxWidth: "100%",              // ← prevents stretching beyond container
                "& p": { mb: 1.5 },           // ← adds breathing room between paragraphs
                "& ul, & ol": { pl: 3, my: 1 },
                "& li": { mb: 0.5 },
                "& a": { color: "primary.main", textDecoration: "underline" },
              }}
                  dangerouslySetInnerHTML={{
                    __html:cleanHTML( selectedProject?.description )|| "",
                  }}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography fontWeight={600} sx={{ mb: 2 }}>
                  {t("pages.home.portfolio.modal.technologies")}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {selectedProject?.technology.map((tag, index) => (
                    <Chip
                      key={`${tag}-${index}`}
                      label={tag}
                      sx={{
                        backgroundColor: theme.palette.custom.tagBg,
                        color: theme.palette.custom.tagText,
                        fontWeight: 500,
                        typography: "textXs",
                      }}
                    />
                  ))}
                </Stack>
              </Box>

              <Box sx={{ mb: 2 }}>
                <Typography fontWeight={600} sx={{ mb: 2 }}>
                  {t("pages.home.portfolio.modal.project_links")}
                </Typography>
                <ProjectLinks
                  links={selectedProject?.links}
                  stackSx={{
                    spacing: 1,
                    justifyContent: "flex-start",
                  }}
                  chipSx={{
                    "& .MuiChip-icon": {
                      color: theme.palette.primary.main,
                    },
                  }}
                />
              </Box>

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                sx={{ mt: 3 }}
              >
                {/* View Full Details Button */}
                <Button
                  component={Link}
                  to={`/project/${selectedProject._id}`}
                  variant="outlined"
                  fullWidth
                  size="large"
                  endIcon={<OpenInNewIcon />}
                  sx={{
                    borderRadius: "0.75rem",
                    fontWeight: 500,
                    textTransform: "none",
                    typography: "textSm",
                    color: "#1b2532",
                    borderColor: theme.palette.grey[300],
                    py: 1.5,
                  }}
                >
                  View Full Details
                </Button>

                {/* Start Similar Project Button */}
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  endIcon={<ArrowForwardIcon />}
                  // onClick={() => {
                  //   handleClose();
                  //   navigate("/", { state: { scrollTo: "contact" } });
                  // }}
                  onClick={() => {
                    handleClose();
                    navigate("/", {
                      state: {
                        scrollTo: "contact",
                        projectRef: {
                          id: selectedProject._id,
                          title: selectedProject?.title,
                        },
                      },
                    });
                  }}
                  sx={{
                    backgroundImage: theme.palette.custom.buttonGradient,
                    borderRadius: "0.75rem",
                    fontWeight: 500,
                    textTransform: "none",
                    typography: "textSm",
                    "&:hover": {
                      backgroundImage: theme.palette.custom.buttonGradient,
                      opacity: 0.9,
                      boxShadow: "none",
                    },
                    py: 1.5,
                  }}
                >
                  {t("pages.home.portfolio.modal.start_similar")}
                </Button>
              </Stack>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default PortfolioSection;
